import React from 'react';
import { useFormik } from 'formik';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputField from '@components/FormControls/InputField';
import DialogFormField from '@components/FormControls/DialogFormField';
import { useMutation } from 'react-query';
import rootApi from '@utils/baseAPI/WrapperApi';
import { showError, showSuccess } from '@components/notification';
import * as Yup from 'yup';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Grid,
  Box,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';

const DialogsEditCustomerInfo = (props) => {
  const { customer, open, setOpen, refetch } = props;

  const { mutateAsync, isLoading, isSuccess } = useMutation('update-customer-info', (data) => {
    return rootApi.put(`/admin/users/${customer?.id}/profile`, {
      full_name: data.full_name,
      address: data.address,
      date_of_birth: data.date_of_birth || null,
      gender: data.gender
    });
  });

  const formik = useFormik({
    initialValues: {
      full_name: customer?.full_name || '',
      address: customer?.address || '',
      date_of_birth: customer?.date_of_birth
        ? dayjs(customer.date_of_birth).format('YYYY-MM-DD')
        : '',
      gender: customer?.gender || ''
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      full_name: Yup.string()
        .required('Họ tên là bắt buộc')
        .min(2, 'Họ tên phải có ít nhất 2 ký tự')
        .label('Họ tên'),
      address: Yup.string()
        .required('Địa chỉ là bắt buộc')
        .min(5, 'Địa chỉ phải có ít nhất 5 ký tự')
        .label('Địa chỉ'),
      date_of_birth: Yup.string()
        .nullable()
        .test('max-date', 'Ngày sinh không thể là ngày trong tương lai', function (value) {
          if (!value) return true;
          return dayjs(value).isBefore(dayjs(), 'day') || dayjs(value).isSame(dayjs(), 'day');
        })
        .label('Ngày sinh'),
      gender: Yup.string().oneOf(['MALE', 'FEMALE', ''], 'Giới tính không hợp lệ').label('Giới tính')
    }),
    onSubmit: async (values) => {
      try {
        const resp = await mutateAsync(values);
        if (resp?.data?.status?.code === 'success') {
          showSuccess({ message: 'Cập nhật thông tin thành công' });
          handleClose();
        } else {
          showError({
            message: resp?.data?.message || 'Cập nhật thất bại. Vui lòng kiểm tra lại thông tin.'
          });
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Backend API chưa hỗ trợ tính năng này. Vui lòng liên hệ IT để bật API endpoint.';
        showError({ message: errorMessage });
      }
    }
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
    // Refetch after dialog closed to prevent setState on unmounted component
    if (refetch) {
      setTimeout(() => refetch(), 300);
    }
  };

  return (
    <div>
      <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
        <DialogTitle>Cập nhật thông tin khách hàng</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Left side - CCCD Image */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                  height: 'fit-content'
                }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  CCCD Mặt trước
                </Typography>
                {customer?.front_cccd ? (
                  <Box
                    component="img"
                    src={customer.front_cccd}
                    alt="CCCD mặt trước"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      border: '1px solid #ddd',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.9
                      }
                    }}
                    onClick={() => window.open(customer.front_cccd, '_blank')}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <Typography variant="body2" color="text.secondary">
                      Chưa có ảnh CCCD
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Right side - Form fields */}
            <Grid item xs={12} md={7}>
              <DialogFormField
                onCloseDialog={handleClose}
                formik={formik}
                isLoading={isLoading}
                isSuccess={isSuccess}
                actionName="Cập nhật">
                <InputField
                  formik={formik}
                  name="full_name"
                  value={formik.values.full_name}
                  onBlur={formik.handleBlur}
                  error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                  helperText={formik.touched.full_name && formik.errors.full_name}
                  required
                  label="Họ và tên"
                  col={12}
                />

                <FormControl
                  fullWidth
                  margin="normal"
                  error={formik.touched.gender && Boolean(formik.errors.gender)}>
                  <InputLabel>Giới tính</InputLabel>
                  <Select
                    name="gender"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Giới tính">
                    <MenuItem value="">Chọn giới tính</MenuItem>
                    <MenuItem value="MALE">Nam</MenuItem>
                    <MenuItem value="FEMALE">Nữ</MenuItem>
                  </Select>
                  {formik.touched.gender && formik.errors.gender && (
                    <FormHelperText>{formik.errors.gender}</FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  margin="normal"
                  name="date_of_birth"
                  label="Ngày sinh"
                  type="date"
                  value={formik.values.date_of_birth}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.date_of_birth && Boolean(formik.errors.date_of_birth)}
                  helperText={formik.touched.date_of_birth && formik.errors.date_of_birth}
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    max: dayjs().format('YYYY-MM-DD')
                  }}
                />

                <InputField
                  formik={formik}
                  name="address"
                  value={formik.values.address}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  required
                  label="Địa chỉ"
                  col={12}
                  multiline
                  rows={3}
                />
              </DialogFormField>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DialogsEditCustomerInfo;
